/* global axios */
import { DataManager } from '../helper/CacheHelper/DataManager';
import ApiClient from './ApiClient';
import { sharedRequest } from './sharedRequest';

class CacheEnabledApiClient extends ApiClient {
  constructor(resource, options = {}) {
    super(resource, options);
    this.dataManager = new DataManager(this.accountIdFromRoute);
  }

  syncDataManagerAccount() {
    if (this.dataManager.accountId !== this.accountIdFromRoute) {
      this.dataManager = new DataManager(this.accountIdFromRoute);
    }
  }

  // eslint-disable-next-line class-methods-use-this
  get cacheModelName() {
    throw new Error('cacheModelName is not defined');
  }

  get(cache = false) {
    this.syncDataManagerAccount();
    const scope = {
      url: this.url,
      accountId: this.accountIdFromRoute,
      dataManager: this.dataManager,
    };
    return sharedRequest(`${scope.url}:${cache}`, () =>
      cache ? this.getFromCache(scope) : this.getFromNetwork(scope.url)
    );
  }

  getFromNetwork(url = this.url) {
    return sharedRequest(url, () => axios.get(url));
  }

  // eslint-disable-next-line class-methods-use-this
  extractDataFromResponse(response) {
    return response.data.payload;
  }

  // eslint-disable-next-line class-methods-use-this
  marshallData(dataToParse) {
    return { data: { payload: dataToParse } };
  }

  async getFromCache(
    scope = {
      url: this.url,
      accountId: this.accountIdFromRoute,
      dataManager: this.dataManager,
    }
  ) {
    const { url, accountId, dataManager } = scope;
    try {
      await dataManager.initDb();
    } catch {
      return this.getFromNetwork(url);
    }

    const cacheUrl = `/api/v1/accounts/${accountId}/cache_keys`;
    const { data } = await sharedRequest(cacheUrl, () => axios.get(cacheUrl));
    const cacheKeyFromApi = data.cache_keys[this.cacheModelName];
    const cacheKey = await dataManager.getCacheKey(this.cacheModelName);
    const localData =
      cacheKeyFromApi === cacheKey
        ? await dataManager.get({ modelName: this.cacheModelName })
        : [];

    if (localData.length === 0)
      return this.refetchAndCommit(cacheKeyFromApi, scope);
    return this.marshallData(localData);
  }

  async refetchAndCommit(newKey = null, scope = null) {
    this.syncDataManagerAccount();
    const { url, dataManager } = scope || {
      url: this.url,
      dataManager: this.dataManager,
    };
    const response = await this.getFromNetwork(url);
    try {
      await dataManager.initDb();
      await dataManager.replace({
        modelName: this.cacheModelName,
        data: this.extractDataFromResponse(response),
      });
      await dataManager.setCacheKeys({ [this.cacheModelName]: newKey });
    } catch {
      // IndexedDB is optional; retain the network response.
    }
    return response;
  }

  async validateCacheKey(cacheKeyFromApi) {
    this.syncDataManagerAccount();
    const { dataManager } = this;
    await dataManager.initDb();
    return (
      cacheKeyFromApi === (await dataManager.getCacheKey(this.cacheModelName))
    );
  }
}

export default CacheEnabledApiClient;
